import { useState, useEffect } from 'react'
import type { ReactNode } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { TitanLogo } from '@components/ui/TitanLogo'
import { LanguageSwitcher } from '@components/ui/LanguageSwitcher'
import { useDirectionList } from '@shared/hooks/useDirectionList'
import { useLang, withLang } from '@shared/i18n/useLang'
import { SITE_URL } from '@shared/config/env'

const ORGANIZATION_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Титан',
  legalName: 'ООО «Титан»',
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  email: 'mail@titan-top.ru',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Двинская ул., д. 10 к. 3, лит. А, пом. 10-Н, офис 729',
    addressLocality: 'Санкт-Петербург',
    postalCode: '198035',
    addressCountry: 'RU',
  },
}

function isActive(to: string, pathname: string, homePath: string) {
  return to === homePath ? pathname === to : pathname.startsWith(to)
}

export function Layout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const { directions, status } = useDirectionList()
  const { t } = useTranslation()
  const lang = useLang()
  const homePath = withLang(lang, '/')

  const navLinks = [
    { label: t('common.home'), to: homePath },
    ...directions.map(d => ({ label: d.name, to: withLang(lang, `/napravleniya/${d.slug}`) })),
    { label: t('common.kontakty'), to: withLang(lang, '/kontakty') },
  ]
  const zayavkaLink = withLang(lang, '/zayavka')

  // close menu on navigation
  useEffect(() => { setOpen(false) }, [pathname])
  // prevent body scroll when open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [open])

  if (status === 'loading') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          width: 40, height: 40, borderRadius: '50%',
          border: '3px solid #D9DEE2',
          borderTopColor: '#189BD5',
          animation: 'spin 0.8s linear infinite',
        }} />
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', background: '#EEF1F3' }}>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(ORGANIZATION_JSON_LD)}</script>
      </Helmet>

      <header style={{ background: '#171C22', borderBottom: '2px solid #189BD5', position: 'sticky', top: 0, zIndex: 50 }}>
        <div className="hdr-wrap" style={{ position: 'relative', height: 72, display: 'flex', alignItems: 'center', gap: 24 }}>

          {/* Logo */}
          <Link to={withLang(lang, '/')} style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
            <span style={{ width: 42, height: 42, background: '#FFFFFF', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              <TitanLogo size={34} />
            </span>
            <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <span style={{ color: '#FFFFFF', fontSize: 18, fontWeight: 700, letterSpacing: '.08em', lineHeight: 1 }}>{t('layout.brand')}</span>
              <span className="hdr-sub" style={{ color: '#93A3AE', fontSize: 10, fontWeight: 500, letterSpacing: '.14em', textTransform: 'uppercase', lineHeight: 1.3 }}>{t('layout.brandSub')}</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hdr-nav" style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', alignItems: 'center', gap: 2 }}>
            {navLinks.map(l => {
              const active = isActive(l.to, pathname, homePath)
              return (
                <Link key={l.to} to={l.to} style={{
                  fontSize: 12, fontWeight: 500, letterSpacing: '.05em', textTransform: 'uppercase',
                  padding: '9px 11px', borderRadius: 3,
                  color: active ? '#FFFFFF' : '#93A3AE',
                  boxShadow: active ? 'inset 0 -2px 0 #189BD5' : 'none',
                  transition: 'color .15s', whiteSpace: 'nowrap',
                }}>
                  {l.label}
                </Link>
              )
            })}
          </nav>

          {/* Desktop CTA + language switcher */}
          <div className="hdr-cta" style={{ marginLeft: 'auto', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
            <LanguageSwitcher />
            <Link to={zayavkaLink} style={{
              flexShrink: 0, background: '#189BD5', color: '#FFFFFF',
              fontSize: 12, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase',
              padding: '11px 20px', borderRadius: 3, transition: 'background .15s',
            }}>
              {t('common.submitApplication')}
            </Link>
          </div>

          {/* Burger */}
          <button
            className="hdr-burger"
            onClick={() => setOpen(o => !o)}
            aria-label={t('layout.menuAria')}
            style={{
              marginLeft: 'auto', width: 44, height: 44,
              background: 'transparent', border: '1px solid #3A434D',
              borderRadius: 3, cursor: 'pointer',
              flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 5, padding: 0, transition: 'border-color .15s',
            }}
          >
            <span style={{ display: 'block', width: 20, height: 2, background: '#FFFFFF', transition: 'transform .2s,opacity .2s', transform: open ? 'translateY(7px) rotate(45deg)' : 'none' }} />
            <span style={{ display: 'block', width: 20, height: 2, background: '#FFFFFF', transition: 'transform .2s,opacity .2s', opacity: open ? 0 : 1 }} />
            <span style={{ display: 'block', width: 20, height: 2, background: '#FFFFFF', transition: 'transform .2s,opacity .2s', transform: open ? 'translateY(-7px) rotate(-45deg)' : 'none' }} />
          </button>
        </div>

        {/* Overlay */}
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0, top: 74,
            background: 'rgba(13,17,21,.6)', zIndex: 40,
            transition: 'opacity .25s',
            opacity: open ? 1 : 0, pointerEvents: open ? 'auto' : 'none',
          }}
        />

        {/* Mobile panel */}
        <div style={{
          position: 'fixed', top: 74, right: 0, bottom: 0,
          width: 'min(320px, 85vw)',
          background: '#171C22', borderLeft: '1px solid #262D35',
          zIndex: 45, display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
          overflowY: 'auto',
          transition: 'transform .25s ease',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
        }}>
          <nav style={{ display: 'flex', flexDirection: 'column', padding: '16px 0' }}>
            {navLinks.map(l => {
              const active = isActive(l.to, pathname, homePath)
              return (
                <Link key={l.to} to={l.to} style={{
                  fontSize: 14, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase',
                  padding: '15px 24px', transition: 'color .15s, background .15s',
                  color: active ? '#FFFFFF' : '#93A3AE',
                  boxShadow: active ? 'inset 3px 0 0 #189BD5' : 'none',
                  background: active ? '#1E252C' : 'transparent',
                }}>
                  {l.label}
                </Link>
              )
            })}
          </nav>
          <div style={{ padding: '16px 24px 24px', borderTop: '1px solid #262D35', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <LanguageSwitcher compact />
            <Link to={zayavkaLink} style={{
              textAlign: 'center', background: '#189BD5', color: '#FFFFFF',
              fontSize: 13, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase',
              padding: '15px 24px', borderRadius: 3,
            }}>
              {t('common.submitApplication')}
            </Link>
          </div>
        </div>
      </header>

      {children}
    </div>
  )
}
